import {
    type AppAuthentication,
    type ClassScreenDescription,
    type ScreenDescription,
    type TabDescription,
    ScreenDescriptionKind,
    getScreenProperty,
    makeScreenProperty,
} from "@glide/app-description";
import type { AppSettings, PageTemplateKind } from "@glide/common-core/dist/js/firebase-function-types";
import { chatScreenName, isAddOrEditOrFormScreenDescription, shoppingCartScreenName } from "@glide/function-utils";
import { assert, assertNever, defined } from "@glideapps/ts-necessities";
import { makeUniqueRandomName } from "@glide/support";
import randomSample from "lodash/sample";
import { randomTheme } from "@glide/common";
import type { BaseTheme } from "@glide/base-theme";

export const MAX_TABS = 4;

export function isChatTab(tab: TabDescription): boolean {
    return getScreenProperty(tab.screenName) === chatScreenName;
}

export function isShoppingCartTab(tab: TabDescription): boolean {
    return getScreenProperty(tab.screenName) === shoppingCartScreenName;
}

export function copyToFreeScreen(screenName: string, screen: ClassScreenDescription): ScreenDescription {
    assert(!isAddOrEditOrFormScreenDescription(screenName, screen));
    const newScreen = { ...screen, fetchesData: true };
    if (newScreen.kind === ScreenDescriptionKind.Class) {
        return { ...newScreen, title: undefined };
    } else {
        return newScreen;
    }
}

export function addOrRemoveSpecialTabsIfNecessary(
    tabs: readonly TabDescription[],
    isPage: boolean
): readonly TabDescription[] {
    if (isPage) {
        return tabs.filter(t => {
            const screenName = getScreenProperty(t.screenName);
            return screenName !== undefined && screenName !== chatScreenName && screenName !== shoppingCartScreenName;
        });
    } else {
        const newTabs = [...tabs];
        if (!tabs.some(isChatTab)) {
            newTabs.push({
                title: "Chat",
                icon: "message-circle",
                hidden: true,
                inFlyout: false,
                screenName: makeScreenProperty(chatScreenName),
            });
        }
        if (!tabs.some(isShoppingCartTab)) {
            newTabs.push({
                title: "Shopping Cart",
                icon: "09-07-shopping-bag-side",
                hidden: true,
                inFlyout: false,
                screenName: makeScreenProperty(shoppingCartScreenName),
            });
        }
        return newTabs;
    }
}

const defaultEmojis = [
    "💼",
    "🤖",
    "🚀",
    "💎",
    "⚡",
    "✏️",
    "☕",
    "📈",
    "🗒️",
    "🗂️",
    "🏛️",
    "🏬",
    "🚚",
    "✈️",
    "💻",
    "📊",
    "📘",
    "📚",
    "🔔",
    "📫",
];

function applyTemplateKindToTheme(theme: BaseTheme, templateKind: PageTemplateKind | undefined): BaseTheme {
    if (templateKind === undefined) return theme;

    switch (templateKind) {
        case "basic":
        case "directory": {
            theme.pageBackground = "Highlight";
            theme.pageTheme = "Highlight";
            theme.showDesktopSideBar = false;
            break;
        }
        case "form": {
            theme.pageBackground = "Highlight";
            theme.pageTheme = "Accent";
            theme.showDesktopSideBar = false;
            theme.pageContainer = "Narrow";
            break;
        }
        case "blank": {
            theme.pageBackground = "Highlight";
            theme.pageTheme = "Highlight";
            theme.showDesktopSideBar = false;
            break;
        }
        case "portal": {
            theme.pageBackground = "Highlight";
            theme.pageTheme = "Accent";
            theme.showDesktopSideBar = true;
            break;
        }
        case "field operations":
        case "inventory & logistics":
        case "client portal":
        case "event planning":
        case "admin dashboard":
        case "company directory":
        // temporary data source templates
        case "glide-big-tables":
        case "mysql":
        case "postgresql":
        case "sqlserver":
        case "google-cloud-sql":
        case "bigquery":
            theme.pageTheme = "Accent";
            break;
        default:
            assertNever(templateKind);
    }

    return theme;
}

export function makeRandomAppSettings(
    title: string,
    authentication: AppAuthentication | undefined,
    previousTheme: BaseTheme | undefined = undefined,
    templateKind: PageTemplateKind | undefined = undefined,
    primaryAccentColor: string | undefined = undefined
): AppSettings {
    const theme = previousTheme ?? applyTemplateKindToTheme(randomTheme(), templateKind);
    if (primaryAccentColor !== undefined) {
        theme.primaryAccentColor = primaryAccentColor;
    }
    return {
        title,
        iconImage: { emoji: defined(randomSample(defaultEmojis)) },
        theme,
        authentication,
    };
}

export function makeScreenNameWithPrefix(prefix: string, nameExists: (n: string) => boolean): string {
    function makeName(suffix: string) {
        return prefix + suffix;
    }
    const suffix = makeUniqueRandomName(s => nameExists(makeName(s)));
    return makeName(suffix);
}
